<!--
  @component DownloadCenterPanel
  @description 展示可关闭、可恢复且不阻塞页面交互的全局下载任务面板。
-->
<script setup lang="ts">
import { computed } from 'vue'
import {
  CircleCheck,
  CircleClose,
  Close,
  Delete,
  Download,
  Loading
} from '@element-plus/icons-vue'
import {
  useDownloadManager,
  type DownloadTask,
  type DownloadTaskStatus
} from '@/composables/useDownloadManager'

const {
  downloadCenterVisible,
  downloadTasks,
  activeDownloadCount,
  hasFinishedDownloads,
  closeDownloadCenter,
  clearFinishedDownloads
} = useDownloadManager()

/** 下载任务时间格式化器。 */
const taskTimeFormatter = new Intl.DateTimeFormat('zh-CN', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
})

/** 下载面板标题。 */
const panelTitle = computed(() =>
  activeDownloadCount.value > 0
    ? `下载任务（${activeDownloadCount.value} 进行中）`
    : '下载任务'
)

/**
 * 获取下载状态文案。
 * @param status 下载任务执行状态。
 * @returns 中文状态文案。
 */
const getStatusLabel = (status: DownloadTaskStatus): string => {
  const labels: Record<DownloadTaskStatus, string> = {
    queued: '等待中',
    running: '下载中',
    completed: '已完成',
    failed: '失败'
  }
  return labels[status]
}

/**
 * 获取下载状态标签类型。
 * @param status 下载任务执行状态。
 * @returns Element Plus 标签类型。
 */
const getStatusType = (
  status: DownloadTaskStatus
): 'info' | 'warning' | 'success' | 'danger' => {
  const types: Record<
    DownloadTaskStatus,
    'info' | 'warning' | 'success' | 'danger'
  > = {
    queued: 'info',
    running: 'warning',
    completed: 'success',
    failed: 'danger'
  }
  return types[status]
}

/**
 * 获取下载进度条状态。
 * @param status 下载任务执行状态。
 * @returns Element Plus 进度条状态。
 */
const getProgressStatus = (
  status: DownloadTaskStatus
): 'success' | 'exception' | undefined => {
  if (status === 'completed') return 'success'
  if (status === 'failed') return 'exception'
  return undefined
}

/**
 * 获取下载状态图标。
 * @param status 下载任务执行状态。
 * @returns 对应的 Element Plus 图标组件。
 */
const getStatusIcon = (status: DownloadTaskStatus) => {
  if (status === 'completed') return CircleCheck
  if (status === 'failed') return CircleClose
  if (status === 'running') return Loading
  return Download
}

/**
 * 格式化字节体积。
 * @param bytes 原始字节数。
 * @returns 适合界面展示的体积文案。
 */
const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unitIndex = 0
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
}

/**
 * 获取下载任务体积文案。
 * @param task 下载任务。
 * @returns 已下载体积或已下载与总体积文案。
 */
const getTaskSizeText = (task: DownloadTask): string => {
  const loaded = formatBytes(task.progress.loadedBytes)
  const total = formatBytes(task.progress.totalBytes)
  if (loaded && total) return `${loaded} / ${total}`
  return loaded
}

/**
 * 格式化下载任务时间。
 * @param timestamp 下载任务时间戳。
 * @returns 时分秒文案。
 */
const formatTaskTime = (timestamp: number): string =>
  taskTimeFormatter.format(timestamp)
</script>

<template>
  <Teleport to="#tdm-overlay-root">
    <transition name="download-center-motion">
      <section
        v-if="downloadCenterVisible"
        class="download-center"
        aria-live="polite"
        aria-label="下载任务"
      >
        <header class="download-center__header">
          <div class="download-center__heading">
            <el-icon><Download /></el-icon>
            <strong>{{ panelTitle }}</strong>
          </div>
          <div class="download-center__actions">
            <el-button
              v-if="hasFinishedDownloads"
              text
              size="small"
              :icon="Delete"
              @click="clearFinishedDownloads"
            >
              清理记录
            </el-button>
            <el-button
              text
              circle
              :icon="Close"
              aria-label="关闭下载任务面板"
              @click="closeDownloadCenter"
            />
          </div>
        </header>

        <div v-if="downloadTasks.length === 0" class="download-center__empty">
          <el-icon><Download /></el-icon>
          <span>暂无下载任务</span>
        </div>

        <el-scrollbar v-else max-height="min(62vh, 560px)">
          <div class="download-center__list">
            <article
              v-for="task in downloadTasks"
              :key="task.id"
              class="download-task"
              :class="`download-task--${task.status}`"
            >
              <div class="download-task__topline">
                <el-icon
                  class="download-task__status-icon"
                  :class="{ 'is-loading': task.status === 'running' }"
                >
                  <component :is="getStatusIcon(task.status)" />
                </el-icon>
                <strong class="download-task__title" :title="task.title">
                  {{ task.title }}
                </strong>
                <el-tag :type="getStatusType(task.status)" size="small">
                  {{ getStatusLabel(task.status) }}
                </el-tag>
              </div>

              <el-progress
                :percentage="task.progress.percent"
                :status="getProgressStatus(task.status)"
                :stroke-width="7"
              />

              <div class="download-task__meta">
                <span>{{ task.progress.message }}</span>
                <span v-if="getTaskSizeText(task)">{{
                  getTaskSizeText(task)
                }}</span>
                <time :datetime="new Date(task.createdAt).toISOString()">
                  {{ formatTaskTime(task.createdAt) }}
                </time>
              </div>
            </article>
          </div>
        </el-scrollbar>
      </section>
    </transition>
  </Teleport>
</template>

<style scoped lang="scss">
.download-center {
  position: fixed;
  top: calc(var(--tdm-nav-height) + 12px);
  right: 16px;
  z-index: calc(var(--tdm-nav-z) + 20);
  width: min(420px, calc(100vw - 24px));
  overflow: hidden;
  border: 1px solid var(--tdm-border-light);
  border-radius: 14px;
  background: var(--tdm-bg-elevated);
  box-shadow: var(--tdm-shadow-card);
}

.download-center__header {
  display: flex;
  min-height: 52px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 10px 0 16px;
  border-bottom: 1px solid var(--tdm-border-light);
}

.download-center__heading,
.download-center__actions,
.download-task__topline,
.download-task__meta {
  display: flex;
  align-items: center;
}

.download-center__heading {
  min-width: 0;
  gap: 8px;
  color: var(--tdm-text-primary);
}

.download-center__actions {
  flex-shrink: 0;
  gap: 2px;
}

.download-center__empty {
  display: grid;
  min-height: 160px;
  place-content: center;
  justify-items: center;
  gap: 10px;
  color: var(--tdm-text-tertiary);

  .el-icon {
    font-size: 30px;
  }
}

.download-center__list {
  display: grid;
  gap: 10px;
  padding: 12px;
}

.download-task {
  display: grid;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--tdm-border-light);
  border-radius: 10px;
  background: var(--tdm-bg-muted);
}

.download-task--failed {
  border-color: var(--el-color-danger-light-5);
}

.download-task__topline {
  min-width: 0;
  gap: 8px;
}

.download-task__status-icon {
  flex-shrink: 0;
  color: var(--tdm-color-primary);
}

.download-task--completed .download-task__status-icon {
  color: var(--el-color-success);
}

.download-task--failed .download-task__status-icon {
  color: var(--el-color-danger);
}

.download-task__title {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: var(--tdm-text-primary);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.download-task__meta {
  min-width: 0;
  gap: 8px;
  color: var(--tdm-text-secondary);
  font-size: 12px;

  span:first-child {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  time {
    flex-shrink: 0;
  }
}

.download-center-motion-enter-active,
.download-center-motion-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.download-center-motion-enter-from,
.download-center-motion-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

@media (max-width: 768px) {
  .download-center {
    top: calc(var(--tdm-nav-height) + 8px);
    right: 8px;
    width: calc(100vw - 16px);
  }

  .download-center__header {
    padding-left: 12px;
  }
}
</style>
